import type { CheckoutGolfService, GolfServiceSelectedValue } from '@/scopes/checkout/domain/types'
import { DateFormat } from '@/scopes/common/domain/types'
import { isValidDate, parseDay } from '@/scopes/common/domain/utils/dates'
import { format } from 'date-fns'

/** Returns a human-readable summary for a saved golf slot, or null if the slot has no selection */
export function getGolfSlotSummary(
  service: CheckoutGolfService | null,
  slotId: string,
): { locationName: string, date: string, time: string, holes: number, withBuggy: boolean, participantIds: string[] } | null {
  if (!service)
    return null
  const slot = service.selectedValues[slotId]
  if (!slot?.optionId)
    return null
  const option = service.options[slot.optionId]
  if (!option)
    return null
  return {
    locationName: service.locations[slot.locationId]?.name ?? slot.locationId,
    date: slot.date,
    time: slot.time,
    holes: slot.holes,
    withBuggy: slot.withBuggy,
    participantIds: slot.participantIds,
  }
}

/** Returns whether a slot has all required fields filled in and can be saved */
export function golfSlotIsComplete(slot: GolfServiceSelectedValue | undefined): boolean {
  if (!slot)
    return false
  return Boolean(slot.optionId && slot.participantIds.length > 0)
}

/**
 * Formats an ISO date string (e.g. "2024-06-15") as a locale-aware display date.
 * Falls back to the raw string when the date is invalid.
 */
export function formatGolfDate(isoDate: string, locale?: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate)
  if (!match)
    return isoDate
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const d = new Date(Date.UTC(year, month - 1, day))
  if (
    Number.isNaN(d.getTime())
    || d.getUTCFullYear() !== year
    || d.getUTCMonth() !== month - 1
    || d.getUTCDate() !== day
  ) {
    return isoDate
  }
  return d.toLocaleDateString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

/** Parses a date string in the given UI dateFormat and returns it as an ISO yyyy-MM-dd string */
export function parseGolfDateToISO(date: string, dateFormat: DateFormat): string {
  const parsed = parseDay(date, dateFormat)
  if (!isValidDate(parsed))
    return ''
  return format(parsed, 'yyyy-MM-dd')
}
